"use client";

/**
 * AdminDateInput — 날짜 입력 공통 컴포넌트 (type="date")
 *
 * 브라우저 기본 날짜 표시(로케일 의존 점 형식)를 숨기고,
 * 직접 "YYYY-MM-DD" 형식 또는 placeholder 를 그린다.
 * 클릭 시 네이티브 달력 picker 를 연다.
 *
 * 값은 "YYYY-MM-DD" 문자열로 주고받는다.
 *
 * @example
 * const [date, setDate] = useState("");
 * <AdminDateInput
 *   value={date}
 *   onChange={setDate}
 *   placeholder="날짜를 선택하세요"
 * />
 *
 * @example // 범위 제한
 * <AdminDateInput value={start} onChange={setStart} max={end} />
 * <AdminDateInput value={end}   onChange={setEnd}   min={start} />
 */
import { forwardRef } from "react";

import { cn } from "../../lib/utils";

export interface AdminDateInputProps {
    /** "YYYY-MM-DD" 형식 날짜 문자열 */
    value?: string;
    onChange?: (value: string) => void;
    /** 값이 없을 때 표시할 안내 문구 (기본 "날짜를 선택하세요") */
    placeholder?: string;
    /** 선택 가능한 최솟값 ("YYYY-MM-DD") */
    min?: string;
    /** 선택 가능한 최댓값 ("YYYY-MM-DD") */
    max?: string;
    disabled?: boolean;
    /** 추가 CSS 클래스 */
    className?: string;
    "aria-label"?: string;
    id?: string;
}

/** 클릭 시 네이티브 달력을 연다. */
function openPicker(e: React.MouseEvent<HTMLInputElement>) {
    try {
        (e.currentTarget as HTMLInputElement).showPicker?.();
    } catch {
        /* showPicker 미지원 시 기본 동작 유지 */
    }
}

export const AdminDateInput = forwardRef<HTMLInputElement, AdminDateInputProps>(
    function AdminDateInput(
        {
            value = "",
            onChange,
            placeholder = "날짜를 선택하세요",
            min,
            max,
            disabled,
            className,
            "aria-label": ariaLabel,
            id,
        },
        ref
    ) {
        return (
            <input
                ref={ref}
                id={id}
                type="date"
                aria-label={ariaLabel}
                value={value}
                min={min}
                max={max}
                disabled={disabled}
                onChange={(e) => onChange?.(e.target.value)}
                onClick={openPicker}
                // data-display: ::before 가상 요소가 이 값을 직접 렌더링한다 (globals.css .admin-date-input)
                data-display={value || placeholder}
                // data-empty: placeholder 일 때 회색으로 표시한다
                data-empty={value ? "false" : "true"}
                className={cn(
                    "admin-date-input",
                    // shadcn Input 기본 스타일에 맞춤
                    "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none",
                    "focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50",
                    "disabled:pointer-events-none disabled:opacity-50",
                    className
                )}
            />
        );
    }
);
